import { I18n } from "@assets/strings";
import React from "react";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { ActionInfo, Carousel } from "@domain/carousel/carousel";
import { Article } from "@domain/lms/article/article";
import { useAsync, useIsMobile } from "@utils/hooks";
import { readableCount } from "@utils/readableCount";
import { useModalTools } from "@ui/modals/modalContext";
import { AuthModal } from "@ui/components/auth/authModal";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { ActiveInstructiveIcon, InstructiveIcon } from "./icons";

type Content =
	| Carousel
	| (Article & {
			instructives: ActionInfo;
	  });

export interface InstructiveButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "content"> {
	content: Content;
	toggleInstructive: () => Promise<void>;
	isAuthor: boolean;
	isAuthenticated: boolean;
}

export const InstructiveButton: React.FC<InstructiveButtonProps> = ({
	content,
	isAuthor,
	isAuthenticated,
	toggleInstructive,
	...htmlProps
}) => {
	const isMobile = useIsMobile();
	const { createModal } = useModalTools();

	const { instructives } = content;
	const { trackingService } = useServices();
	const { run, loading } = useAsync(async () => {
		await toggleInstructive();
		trackingService.track("Carousel - useful ");
	}, [content]);

	return (
		<StyledButton
			{...htmlProps}
			isInstructive={instructives?.byMe}
			isAuthor={isAuthor}
			isAuthenticated={isAuthenticated}
			onClick={() => {
				if (!isAuthenticated) {
					createModal((close) => <AuthModal close={close} />);
					return;
				}
				if (isAuthor) return;
				if (loading) return;
				run();
			}}
			isMobile={isMobile}
		>
			<InstructiveContainer>
				{!instructives?.byMe && !isAuthor && isAuthenticated ? (
					<>
						<InstructiveIcon /> {I18n.t("carousel.insightful.tooltip.label.IfInsightful")}
					</>
				) : (
					<>
						<ActiveInstructiveIcon /> {readableCount(instructives?.total)}
					</>
				)}
			</InstructiveContainer>
		</StyledButton>
	);
};

const InstructiveContainer = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`;

const StyledButton = styled(LoadingButton)<{
	isInstructive: boolean;
	isMobile: boolean;
	isAuthor: boolean;
	isAuthenticated: boolean;
}>`
	background-color: ${({ isInstructive, isAuthor, isAuthenticated, theme }) =>
		(isInstructive || isAuthor || !isAuthenticated) && `${theme.colors.thirded}`};
	padding: 8px 12px;
	${({ isInstructive, isAuthor, isAuthenticated, theme }) =>
		(isInstructive || isAuthor || !isAuthenticated) &&
		css`
			:hover {
				background-color: ${theme.colors.lightthirded};
			}
		`};

	${({ isMobile }) =>
		isMobile &&
		css`
			margin-right: 11px;
		`};
`;
